import httpClient from './httpClient';

/**
 * Parse history data from backend
 * Handles the new warehouse_movements-based structure
 */
const parseHistoryData = (history) => {
  if (!Array.isArray(history)) return [];

  return history.map((item) => {
    // Backend now returns properly structured data from warehouse_movements
    return {
      _id: item._id || null,
      movement_id: item.movement_id || '',
      product_id: item.product_id || '',
      change_type: item.change_type || item.movement_type || '',
      movement_type: item.movement_type || '',
      source_type: item.source_type || '',
      source_reference: item.source_reference || '',
      quantity_change: item.quantity_change ?? 0,
      quantity: item.quantity ?? Math.abs(item.quantity_change ?? 0),
      stock_before: item.stock_before ?? null,
      stock_after: item.stock_after ?? null,
      unit: item.unit || '',
      location: item.location || '',
      batch_number: item.batch_number || '',
      comments: item.comments || item.notes || '',
      operator_id: item.operator_id || '',
      operator_name: item.operator_name || 'Unknown',
      client_id: item.client_id || '',
      client_name: item.client_name || '',
      supplier_id: item.supplier_id || '',
      supplier_name: item.supplier_name || '',
      status: item.status || '',
      created_at: item.created_at || item.movement_date,
      movement_date: item.movement_date || item.created_at,
      product_name: item.product_name || '',
      specification: item.specification || '',
      unit_price: item.unit_price || 0,
    };
  });
};

/**
 * Calculate stock_before and stock_after for history items
 * Works backwards from current stock using quantity_change
 * @param {Array} history - Array of history items (sorted newest first)
 * @param {number} currentStock - Current stock quantity at the location
 * @returns {Array} - History items with calculated stock_before/stock_after
 */
const calculateStockLevels = (history, currentStock) => {
  if (!Array.isArray(history) || history.length === 0) {
    return history;
  }

  // Sort by date (newest first) to calculate backwards
  const sortedHistory = [...history].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA; // Newest first
  });

  let stockTracker = currentStock;

  // Calculate stock levels working backwards from current stock
  const calculated = sortedHistory.map((item) => {
    const stockAfter = stockTracker;
    // quantity_change is positive for inbound, negative for outbound
    // So stock_before = stock_after - quantity_change
    const stockBefore = stockAfter - (item.quantity_change || 0);

    // Update tracker for next iteration (going backwards in time)
    stockTracker = stockBefore;

    return {
      ...item,
      stock_before: Math.round(stockBefore),
      stock_after: Math.round(stockAfter)
    };
  });

  // Return in original order
  return calculated;
};

/**
 * Get inventory history for a specific product
 * @param {string} productId - Product ID (product_id string)
 * @param {number} page - Page number (1-based)
 * @param {number} pageSize - Number of items per page (default: 10)
 * @param {string} sortBy - Sort field (default: 'created_at')
 * @param {string} sortOrder - Sort order 'asc' or 'desc' (default: 'desc')
 * @param {object} filters - Optional filters
 * @param {string} location - Optional location filter
 * @param {number} currentStock - Current stock at the location (for stock calculation)
 * @returns {object} - History data with pagination
 */
const getInventoryHistory = async (productId, page = 1, pageSize = 10, sortBy = 'created_at', sortOrder = 'desc', filters = {}, location = null, currentStock = null) => {
  try {
    const params = {
      page,
      page_size: pageSize,
      sort_by: sortBy,
      sort_order: sortOrder,
      change_type: filters.change_type || undefined,
      movement_type: filters.movement_type || undefined,
      operator: filters.operator || undefined,
      client_id: filters.client_id || undefined,
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined,
      location: location || undefined,
    };

    // Remove undefined parameters
    Object.keys(params).forEach(key => {
      if (params[key] === undefined) {
        delete params[key];
      }
    });

    const response = await httpClient.get(`/api/inventory/history/${productId}`, { params });
    const payload = response.data?.data || response.data || {};

    // Extract history array
    const rawHistory = payload.history || [];
    let normalized = parseHistoryData(rawHistory);

    // Calculate stock_before/stock_after if location and currentStock are provided
    if (location && currentStock !== null && currentStock !== undefined) {
      normalized = calculateStockLevels(normalized, currentStock);
    }

    // Extract pagination info
    const pagination = payload.pagination || {};
    const pageNum = pagination.page ?? page;
    const pageSizeNum = pagination.page_size ?? pageSize;
    const total = pagination.total_count ?? normalized.length;
    const hasNext = pagination.has_next ?? false;
    const hasPrev = pagination.has_prev ?? false;

    // Extract product info (backend returns 'product', not 'product_info')
    const productInfo = payload.product || payload.product_info || {};

    return {
      history: normalized,
      pagination: {
        page: pageNum,
        page_size: pageSizeNum,
        total_count: total,
        has_next: hasNext,
        has_prev: hasPrev,
      },
      product: {
        _id: productInfo._id || '',
        product_id: productInfo.product_id || productId,
        name: productInfo.name || '',
        specification: productInfo.specification || '',
        unit: productInfo.unit || '',
        current_stock: productInfo.current_stock || 0,
      },
      summary: payload.summary || undefined,
    };
  } catch (error) {
    console.error('Failed to fetch inventory history:', error);
    throw error;
  }
};

/**
 * Get available change types for filtering
 * @returns {Array<string>} - Array of change type options
 */
const getChangeTypes = () => {
  return [
    'sale',
    'purchase',
    'inbound',
    'outbound',
    'adjustment',
    'return',
    'transfer',
    'manual',
    'purchase_order',
    'sales_order',
    'manual_adjustment',
    'sales_fulfillment',
    'purchase_receipt',
  ];
};

/**
 * Get formatted change type labels for display
 * @param {string} changeType - The change type value
 * @returns {string} - Formatted display label
 */
const getChangeTypeLabel = (changeType) => {
  const labels = {
    sale: '销售出库',
    purchase: '采购入库',
    inbound: '入库',
    outbound: '出库',
    adjustment: '调整',
    return: '退货',
    transfer: '调拨',
    manual: '手动',
    purchase_order: '采购单',
    sales_order: '销售单',
    manual_adjustment: '手动调整',
    sales_fulfillment: '销售出库',
    purchase_receipt: '采购入库',
  };

  // Handle types with suffixes (e.g., "purchase_order:PO-123")
  const baseType = (changeType || '').split(':')[0];
  return labels[baseType] || changeType || '未知';
};

/**
 * Get change type color for UI display
 * @param {string} changeType - The change type value
 * @returns {string} - Material-UI color name
 */
const getChangeTypeColor = (changeType) => {
  const colors = {
    sale: 'error',          // Red for outgoing stock
    outbound: 'error',      // Red for outgoing stock
    purchase: 'success',    // Green for incoming stock
    inbound: 'success',     // Green for incoming stock
    adjustment: 'warning',  // Orange for adjustments
    return: 'info',         // Blue for returns
    transfer: 'secondary',  // Gray for transfers
    manual: 'default',
    purchase_order: 'success',
    sales_order: 'error',
    manual_adjustment: 'warning',  // Orange for manual adjustments
    sales_fulfillment: 'error',    // Red for sales outbound
    purchase_receipt: 'success',   // Green for purchase inbound
  };

  const baseType = (changeType || '').split(':')[0];
  return colors[baseType] || 'default';
};

/**
 * Format quantity change with appropriate sign and styling
 * @param {string} changeType - The change type
 * @param {number} quantityChange - The quantity change amount
 * @returns {object} - Formatted display data with text, color, and sign
 */
const formatQuantityChange = (changeType, quantityChange) => {
  const change = quantityChange ?? 0;

  // Determine if this is a positive (inbound) or negative (outbound) change
  const isPositive = change >= 0;
  const absChange = Math.abs(change);

  // Display with appropriate sign
  const displayValue = isPositive ? `+${absChange}` : `-${absChange}`;

  return {
    text: displayValue,
    color: isPositive ? 'success.main' : 'error.main',
    sign: isPositive ? 'positive' : 'negative'
  };
};

/**
 * Format date for display (Beijing time)
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date string
 */
const formatDate = (date) => {
  if (!date) return '';

  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';

    // Add 8 hours for Beijing time (UTC+8)
    const beijingTime = new Date(dateObj.getTime() + (8 * 60 * 60 * 1000));

    return beijingTime.toISOString().replace('T', ' ').substring(0, 19);
  } catch (error) {
    console.error('Date formatting error:', error);
    return '';
  }
};

/**
 * Export history data to CSV
 * @param {Array} historyItems - Array of history items
 * @param {object} productInfo - Product information for filename
 * @returns {boolean} - Success status
 */
const exportHistoryToCSV = (historyItems, productInfo = {}) => {
  try {
    const headers = [
      '日期',
      '类型',
      '来源',
      '库存变化',
      '数量',
      '单位',
      '位置',
      '批次号',
      '客户',
      '供应商',
      '备注',
      '操作员'
    ];

    const csvContent = [
      headers.join(','),
      ...historyItems.map(item => [
        `"${formatDate(item.created_at)}"`,
        `"${getChangeTypeLabel(item.change_type)}"`,
        `"${item.source_reference || ''}"`,
        `"${formatQuantityChange(item.change_type, item.quantity_change).text}"`,
        item.quantity || 0,
        `"${item.unit || ''}"`,
        `"${item.location || ''}"`,
        `"${item.batch_number || ''}"`,
        `"${item.client_name || ''}"`,
        `"${item.supplier_name || ''}"`,
        `"${item.comments || ''}"`,
        `"${item.operator_name || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const filename = `inventory_history_${productInfo.product_id || 'unknown'}_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

    return true;
  } catch (error) {
    console.error('Failed to export history to CSV:', error);
    return false;
  }
};

const inventoryHistoryService = {
  getInventoryHistory,
  getChangeTypes,
  getChangeTypeLabel,
  getChangeTypeColor,
  formatQuantityChange,
  formatDate,
  exportHistoryToCSV,
};

export default inventoryHistoryService;
